import { ContentType } from "@/api/scode/scode_params";
import { usePosterSwipePro } from "@/components/poster-swiper/pro";
import { onShareAppMessageByRanking } from "@/utils/actions";
import { platform } from "@/utils/platform";
import { useShare } from "@/utils/share/share";
import { versionsCompare } from "@/utils/version-compare";
import { useEffect, useRef } from "react";

import { getAppSystem } from "@/utils/app-system";
/**
 * 单个榜单分享Hook
 * 用于处理榜单页面的分享逻辑，包括榜单分享和商品分享
 */
export interface RankShareConfig {
  // 榜单分享配置
  rankShare: {
    contentType: ContentType; // 内容类型：榜单中心或类目榜单
    contentId: string; // 内容ID：榜单ID
    // title?: string; // 分享标题
    marketName: string; // 市场名称：榜单中心或榜单详情
  };
}

/**
 * 单个榜单分享Hook
 * @param config 榜单分享配置
 * @param config.rankShare 榜单分享配置
 * @param config.rankShare.contentType 榜单分享类型，只能传ContentType.categoryRank
 * @param config.rankShare.contentId 榜单分享内容ID，传rankNo
 * @param config.rankShare.marketName 榜单分享市场名称，仅单榜单分享时需要传
 * @returns 分享相关的控制器和方法
 */
export const useRankShare = (config: RankShareConfig) => {
  const { rankShare } = config;
  const productRef = useRef<any>(null);
  const { shareProduct, sharePageContent } = useShare();


  // 榜单、商品各生成一套配置，用于不同的分享场景
  // 榜单分享海报配置，要配合@/components/poster-swiper/pro组件使用
  const swiperControl = usePosterSwipePro({
    defaultBizInfo: {
      contentType: rankShare.contentType,
      contentExtra: {
        posterCode:"CategoryHotListPoster",
      },
    },
    defaultEnvContext: {
      sourceScene: rankShare.contentType,
      shareFrom: "MANUAL",
      scodeType: "SHARE",
      targetTerminalType: "H5",
    },
    defaultCarrierContext: {
      carrierElements: ["h5POSTER"],
      posterInfo: {
        posterCode: "CategoryHotListPoster",
      },
    },
    defaultContentContext: {
      contentType: rankShare.contentType,
      contentExtra: {
        posterCode:"CategoryHotListPoster",
      },
    },
    defaultUIConfig: {
      btnConfig: ["copy", "edit"],
    },
    onNonH5Open: async options => {
      // 确保contentId有值
      if (!rankShare.contentId) {
        console.warn("非H5榜单分享失败：contentId为空");
        return;
      }



      sharePageContent({
        miniOptions: {
          contentId: rankShare.contentId || "",
          contentType: rankShare.contentType,
          pageName:  "榜单页",
          shareType: "HOT_LIST_CATEGORY",
          posterCode: "CategoryHotListPoster",
          // contentExtra
        },
        appOptions: {
          contentType: "HOT_LIST_CATEGORY",
          contentId: rankShare.contentId || "",
          pageName:  "榜单页",
          src: "HOT_LIST_CATEGORY",
          sid: rankShare.contentId || "",
          shareType: "HOT_LIST_CATEGORY",
          posterCode: "CategoryHotListPoster",
          imgType: "CategoryHotListPoster",
          // contentExtra
        },
      });
    },
  });

  // 商品分享海报配置，要配合@/components/poster-swiper/pro组件使用
  const productControl = usePosterSwipePro({
    defaultBizInfo: {
      contentType: ContentType.spuProduct,//默认分享类型为商品分享
      contentId: "",
    },
    onNonH5Open: options => {//非H5平台打开海报，是跳到各自的海报页面
      if (!productRef.current) {
        return;
      }
      if (options.platform.isMini) {
        shareProduct({
          miniOptions: {
            ...productRef.current,
          },
        });
      } else if (options.platform.isApp) {
        const {
          activityId,
          activitySpuId,
          selectSkuId,
          spuId,
          skuExtendInfoVO = {},
        } = productRef.current;
        shareProduct({
          appOptions: {
            activityNo: activityId,
            productId: activitySpuId,
            selectSkuId: selectSkuId,
            spuId: spuId,
            src: "HOT_LIST_CATEGORY",
            sid: "",
            marketName: rankShare.marketName,
            marketId: "",
            isOTC: !!skuExtendInfoVO?.isOTC,
          },
        });
      }
    },
  });


  // 点击商卡转发按钮，处理商品分享
  const handleShareProduct = (product: any) => {
    if (!product) {
      return;
    }
    productRef.current = product;
    // 打开分享海报
    productControl.open({
      bizInfo: {
        contentType: ContentType.spuProduct,
        contentId: product.activitySpuId,
        contentDetailId: product.selectSkuId || undefined,
      },
      uiConfig: {
        btnConfig: ["copy", "edit"],
      },
    });
  };

  return {
    swiperControl,
    productControl,
    handleShareProduct,
    productRef,
  };
};
